(function () {
  const SECTION_SELECTOR = '.image-card-tabs';
  const MOBILE_BREAKPOINT = '(min-width: 750px)';

  const initSection = (section) => {
    if (section.dataset.ictInit === '1') return;
    section.dataset.ictInit = '1';

    const cards = Array.from(section.querySelectorAll('.image-card-tabs__card'));
    const panels = Array.from(section.querySelectorAll('.image-card-tabs__panel'));
    const track = section.querySelector('.image-card-tabs__track');
    const stripArrows = Array.from(section.querySelectorAll('.image-card-tabs__arrow--strip'));
    const panelArrows = Array.from(section.querySelectorAll('.image-card-tabs__arrow--panel'));
    if (!cards.length) return;

    /* Panels hold a run of steps: the card's own panel fields are step 1 and each
       `step` block adds one more. The panel arrows walk that run — they no longer
       change the selected card, because a card is picked from the strip. */
    const stepsMode = (section.dataset.panelArrows || 'steps') === 'steps';
    const panelStates = panels.map((panel) => ({
      panel,
      track: panel.querySelector('.image-card-tabs__steps'),
      steps: Array.from(panel.querySelectorAll('.image-card-tabs__step')),
      arrows: Array.from(panel.querySelectorAll('.image-card-tabs__arrow--panel')),
      index: 0
    }));

    const applySteps = (state) => {
      state.steps.forEach((step, i) => {
        step.classList.toggle('is-active', i === state.index);
      });
      /* The slide transition offsets every step by its distance to the current one,
         so the index goes on the track and the CSS does the arithmetic. */
      if (state.track) state.track.style.setProperty('--ict-step-index', state.index);
      if (!stepsMode) return;
      state.arrows.forEach((arrow) => {
        const atEdge = arrow.dataset.nav === 'prev'
          ? state.index === 0
          : state.index >= state.steps.length - 1;
        arrow.disabled = atEdge;
      });
    };

    /* Steps are a sequence, not a loop: the arrows stop at the ends so their disabled
       state is what tells the visitor where they are in the run. */
    const stepBy = (state, dir) => {
      const next = state.index + dir;
      if (next < 0 || next > state.steps.length - 1) return;
      state.index = next;
      applySteps(state);
    };

    const startWithSelection = section.dataset.startWithSelection === '1';
    const defaultIndex = parseInt(section.dataset.defaultIndex || '0', 10);
    let activeIndex = startWithSelection && !isNaN(defaultIndex) ? defaultIndex : null;

    const carouselDesktop = section.dataset.carouselDesktop === '1';
    const carouselMobile = section.dataset.carouselMobile === '1';
    const swiperEl = section.querySelector('.js-ict-swiper');
    const styleProp = (name) => parseFloat(section.style.getPropertyValue(name));
    const colsDesktop = styleProp('--ict-cols-desktop') || 5;
    const colsMobile = styleProp('--ict-cols-mobile') || 2;

    const mql = window.matchMedia(MOBILE_BREAKPOINT);
    let swiper = null;

    const shouldSwipeNow = () =>
      swiperEl && typeof window.Swiper !== 'undefined' &&
      (mql.matches ? carouselDesktop : carouselMobile);

    /* The carousel is a per-breakpoint setting, but Liquid cannot know the
       viewport, so it used to print the Swiper classes whenever *either* switch
       was on. `swiper-bundle.min.css` styles those classes globally
       (`.swiper-wrapper { display: flex }`, `.swiper-slide { width: 100% }`), so
       turning the mobile carousel on also flattened the desktop strip into one
       full-width card per row. The markup is now mode-free and the classes go on
       and off here, which is the only place that knows the viewport. */
    const SWIPER_CLASSES = ['swiper', 'swiper-wrapper', 'swiper-slide'];

    const setSwiperClasses = (on) => {
      if (!swiperEl) return;
      swiperEl.classList.toggle(SWIPER_CLASSES[0], on);
      if (track) track.classList.toggle(SWIPER_CLASSES[1], on);
      cards.forEach((card) => card.classList.toggle(SWIPER_CLASSES[2], on));
    };

    const buildSwiper = () => new window.Swiper(swiperEl, {
      speed: 500,
      spaceBetween: 4,
      /* `'auto'` on mobile so the slide width comes from the stylesheet — the
         same width the native scroller uses, peek included, so flipping the
         carousel does not change how the strip looks. Desktop stays numeric and
         lets Swiper do the maths. */
      slidesPerView: mql.matches ? colsDesktop : 'auto',
      grabCursor: true,
      watchOverflow: true,
      breakpoints: {
        750: { slidesPerView: colsDesktop, spaceBetween: 4 }
      }
    });

    const syncSwiper = () => {
      const need = shouldSwipeNow();
      if (need && !swiper) {
        // Swiper needs `.swiper-wrapper` / `.swiper-slide` present at construction.
        setSwiperClasses(true);
        swiper = buildSwiper();
      } else if (!need && swiper) {
        // `destroy` clears Swiper's own inline styles and classes, not ours.
        swiper.destroy(true, true);
        swiper = null;
        setSwiperClasses(false);
      } else if (swiper) {
        swiper.update();
      }
      syncStripArrows();
    };

    /* The strip arrows are pure navigation: they only exist when the strip has more
       cards than fit on screen. In grid mode it never overflows (the columns shrink),
       so they stay hidden — which is also what keeps them from being the only visible
       affordance on a strip that cannot move. */
    const stripCanScroll = () => {
      if (swiper) return swiper.isLocked !== true && cards.length > 1;
      if (!track) return false;
      return track.scrollWidth - track.clientWidth > 1;
    };

    const syncStripArrows = () => {
      section.dataset.stripOverflow = stripCanScroll() ? '1' : '0';
    };

    const scrollStrip = (dir) => {
      if (swiper) {
        if (dir < 0) swiper.slidePrev();
        else swiper.slideNext();
        return;
      }
      if (!track) return;
      const first = cards[0];
      const step = first && first.offsetWidth ? first.offsetWidth + 4 : track.clientWidth;
      if (track.scrollBy) track.scrollBy({ left: dir * step, behavior: 'smooth' });
      else track.scrollLeft += dir * step;
    };

    const clampIndex = (i) => {
      const n = cards.length;
      return ((i % n) + n) % n;
    };

    const applyState = () => {
      const hasActive = activeIndex !== null;
      section.dataset.hasActive = hasActive ? '1' : '0';

      cards.forEach((card, i) => {
        const active = hasActive && i === activeIndex;
        card.classList.toggle('is-active', active);
        card.setAttribute('aria-selected', active ? 'true' : 'false');
        card.setAttribute('tabindex', active ? '0' : (!hasActive && i === 0 ? '0' : '-1'));
      });

      panels.forEach((panel, i) => {
        const active = hasActive && i === activeIndex;
        panel.classList.toggle('is-active', active);
        if (active) panel.removeAttribute('hidden');
        else panel.setAttribute('hidden', '');
      });
    };

    const activate = (index, opts) => {
      opts = opts || {};
      activeIndex = clampIndex(index);
      /* Picking a material starts its explanation at step 1. */
      if (opts.resetStep !== false) {
        const state = panelStates[activeIndex];
        if (state && state.index !== 0) {
          state.index = 0;
          applySteps(state);
        }
      }
      applyState();
      const card = cards[activeIndex];
      if (opts.focus && card) card.focus();
      if (opts.scroll === false || !card) return;
      if (swiper) {
        swiper.slideTo(activeIndex);
      } else if (card.scrollIntoView) {
        card.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    };

    cards.forEach((card, i) => {
      card.addEventListener('click', () => activate(i));
      card.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          activate((activeIndex == null ? -1 : activeIndex) + 1, { focus: true });
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          activate((activeIndex == null ? 0 : activeIndex) - 1, { focus: true });
        } else if (event.key === 'Home') {
          event.preventDefault();
          activate(0, { focus: true });
        } else if (event.key === 'End') {
          event.preventDefault();
          activate(cards.length - 1, { focus: true });
        } else if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          activate(i);
        }
      });
    });

    /* Strip arrows scroll, they never select: the dimmed-siblings state has to stay a
       consequence of picking a card, not of paging the strip. */
    stripArrows.forEach((arrow) => {
      arrow.addEventListener('click', () => {
        scrollStrip(arrow.dataset.nav === 'prev' ? -1 : 1);
      });
    });

    /* Panel arrows: they step through the active card's explanation. Set the section's
       "Panel arrows" setting to "cards" to get the old behaviour back (paging the
       selection), which is what they did before steps existed. */
    panelArrows.forEach((arrow) => {
      arrow.addEventListener('click', () => {
        const dir = arrow.dataset.nav === 'prev' ? -1 : 1;
        if (stepsMode) {
          const state = panelStates.find((s) => s.panel.contains(arrow));
          if (state) stepBy(state, dir);
          return;
        }
        const base = activeIndex == null ? (dir === 1 ? -1 : 0) : activeIndex;
        activate(base + dir);
      });
    });

    /* Editor: a step is invisible until its panel is open and its own arrow was
       clicked, so selecting the block has to bring it on screen. */
    document.addEventListener('shopify:block:select', (event) => {
      const target = event.target;
      if (!target || !section.contains(target)) return;
      const card = target.closest('.image-card-tabs__card');
      if (card) {
        activate(cards.indexOf(card));
        return;
      }
      const step = target.closest('.image-card-tabs__step');
      if (!step) return;
      const state = panelStates.find((s) => s.steps.indexOf(step) !== -1);
      if (!state) return;
      activate(panels.indexOf(state.panel), { resetStep: false });
      state.index = state.steps.indexOf(step);
      applySteps(state);
    });

    // Swiper wiring & viewport transitions.
    syncSwiper();
    if (mql.addEventListener) mql.addEventListener('change', syncSwiper);
    else mql.addListener(syncSwiper);

    // Overflow depends on the rendered card widths, which settle after layout/fonts.
    let resizeRaf = null;
    const scheduleArrowSync = () => {
      if (resizeRaf) cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(() => {
        resizeRaf = null;
        syncStripArrows();
      });
    };
    window.addEventListener('resize', scheduleArrowSync);
    if (window.ResizeObserver && track) {
      new window.ResizeObserver(scheduleArrowSync).observe(track);
    }
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(scheduleArrowSync).catch(() => {});
    }

    panelStates.forEach(applySteps);
    applyState();
    syncStripArrows();
  };

  const initAll = (root) => {
    const scope = root && root.querySelectorAll ? root : document;
    if (scope.matches && scope.matches(SECTION_SELECTOR)) initSection(scope);
    scope.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document));
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));

  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll(SECTION_SELECTOR).forEach((s) => {
      delete s.dataset.ictInit;
    });
  });
})();
